import React, { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { StickyNote, Plus, Trash2, X } from "lucide-react";

const STORAGE_KEY = "amc_sticky_notes_v1";
const COLORS = [
  { bg: "#FFE873", label: "yellow" },
  { bg: "#FFB4D8", label: "pink" },
  { bg: "#B7F5C2", label: "green" },
  { bg: "#B5E2FF", label: "blue" },
];

function loadAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}
function saveAll(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
}

// Individual sticky note with manual pointer-based dragging.
// Grab the top "DRAG ME" handle to move; the textarea remains fully editable.
function NoteCard({ note, onChange, onDelete }) {
  const nodeRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const dragState = useRef({ active: false, startX: 0, startY: 0, origX: 0, origY: 0, pointerId: null });

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    // Ignore drags that start on interactive children (delete button etc.) so they still fire click.
    if (e.target && e.target.closest && e.target.closest("button")) return;
    e.preventDefault();
    e.stopPropagation();
    const el = nodeRef.current;
    if (!el) return;
    dragState.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      origX: note.x || 0,
      origY: note.y || 0,
      pointerId: e.pointerId,
    };
    try { el.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    setDragging(true);
  };

  const onPointerMove = (e) => {
    if (!dragState.current.active) return;
    const { startX, startY, origX, origY } = dragState.current;
    const nx = origX + (e.clientX - startX);
    const ny = origY + (e.clientY - startY);
    onChange({ x: nx, y: ny });
  };

  const endDrag = (e) => {
    if (!dragState.current.active) return;
    dragState.current.active = false;
    setDragging(false);
    const el = nodeRef.current;
    if (el && dragState.current.pointerId != null) {
      try { el.releasePointerCapture(dragState.current.pointerId); } catch { /* ignore */ }
    }
    dragState.current.pointerId = null;
  };

  return (
    <motion.div
      ref={nodeRef}
      initial={{ opacity: 0, scale: 0.6 }}
      animate={{ opacity: 1, scale: 1, rotate: note.rotate || 0 }}
      exit={{ opacity: 0, scale: 0.6 }}
      transition={{ duration: 0.22 }}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      className={`sticky-note ${dragging ? "dragging" : ""}`}
      style={{ left: note.x, top: note.y, background: note.color, touchAction: "none" }}
      data-testid={`sticky-note-${note.id}`}
    >
      {/* Drag handle — only pointerDown here so drag starts only from this strip.
          Move/up/cancel live on the outer element so pointer capture works. */}
      <div
        onPointerDown={onPointerDown}
        className="absolute top-0 left-0 right-0 h-6 cursor-grab active:cursor-grabbing flex items-center justify-between px-2 rounded-t-sm bg-black/5 hover:bg-black/10 select-none"
        data-testid={`sticky-drag-handle-${note.id}`}
        style={{ touchAction: "none" }}
      >
        <span className="text-[10px] font-mono text-slate-800/70 uppercase tracking-widest">drag</span>
        <button
          onClick={onDelete}
          data-testid={`sticky-delete-${note.id}`}
          className="w-5 h-5 rounded-full flex items-center justify-center bg-black/10 hover:bg-black/25 text-slate-800"
          title="Delete note"
        >
          <Trash2 size={11} />
        </button>
      </div>
      <textarea
        value={note.text}
        onChange={(e) => onChange({ text: e.target.value })}
        placeholder="Your note…"
        data-testid={`sticky-textarea-${note.id}`}
        style={{ marginTop: 22 }}
      />
    </motion.div>
  );
}

export default function StickyNoteWidget({ pageId }) {
  const [allNotes, setAllNotes] = useState(() => loadAll());
  const [open, setOpen] = useState(() => {
    const initial = loadAll();
    return (initial[pageId] || []).length > 0;
  });

  useEffect(() => {
    saveAll(allNotes);
  }, [allNotes]);

  useEffect(() => {
    const existing = allNotes[pageId] || [];
    if (existing.length > 0) setOpen(true);
     
  }, [pageId]);

  const notes = allNotes[pageId] || [];

  const updateNotes = useCallback(
    (updater) => {
      setAllNotes((prev) => {
        const current = prev[pageId] || [];
        const next = typeof updater === "function" ? updater(current) : updater;
        return { ...prev, [pageId]: next };
      });
    },
    [pageId]
  );

  const addNote = () => {
    const id = "n_" + Math.random().toString(36).slice(2, 10);
    const color = COLORS[notes.length % COLORS.length].bg;
    const x = 120 + (notes.length % 4) * 30;
    const y = 140 + (notes.length % 4) * 30;
    updateNotes((cur) => [
      ...cur,
      { id, text: "", color, x, y, rotate: (Math.random() - 0.5) * 6 },
    ]);
    setOpen(true);
  };

  const removeNote = (id) => updateNotes((cur) => cur.filter((n) => n.id !== id));
  const patchNote = (id, patch) =>
    updateNotes((cur) => cur.map((n) => (n.id === id ? { ...n, ...patch } : n)));

  return (
    <>
      <div className="fixed bottom-24 right-5 z-[70] flex flex-col gap-3 items-end" data-testid="sticky-note-toolbar">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setOpen((o) => !o)}
          data-testid="sticky-toggle-btn"
          className="h-12 px-5 rounded-full flex items-center gap-2 bg-slate-900/95 border border-cyan-400/50 text-cyan-200 shadow-lg backdrop-blur font-heading text-sm font-semibold tracking-wide"
          style={{ boxShadow: "0 0 26px rgba(0,240,255,0.45)" }}
          title={open ? "Hide sticky notes on this page" : "Show sticky notes on this page"}
        >
          {open ? <X size={18} /> : <StickyNote size={18} />}
          <span>{open ? "Hide Notes" : notes.length > 0 ? `Notes (${notes.length})` : "Notes"}</span>
        </motion.button>
        {open && (
          <motion.button
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.94 }}
            onClick={addNote}
            data-testid="sticky-add-btn"
            className="px-4 h-11 rounded-full flex items-center gap-2 bg-gradient-to-r from-cyan-400 to-violet-500 text-slate-950 text-sm font-heading font-semibold"
          >
            <Plus size={16} /> New Note
          </motion.button>
        )}
        {open && notes.length === 0 && (
          <div className="text-xs text-slate-400 bg-slate-900/80 backdrop-blur border border-slate-700 px-3 py-2 rounded-lg max-w-[220px]" data-testid="sticky-hint">
            No notes on this page yet. Tap <b>New Note</b> — drag the top strip to move, click text to edit, trash to delete.
          </div>
        )}
      </div>

      <AnimatePresence>
        {open &&
          notes.map((n) => (
            <NoteCard
              key={n.id}
              note={n}
              onChange={(patch) => patchNote(n.id, patch)}
              onDelete={() => removeNote(n.id)}
            />
          ))}
      </AnimatePresence>
    </>
  );
}
